import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { useAppId } from '../context/AppIdContext';
import '../style/speakSearch.css';
import Header from '../component/header'; 

const BASE_URL = process.env.REACT_APP_BASE_URL;

const StarVolunteer = () => {
    const [volunteers, setVolunteers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const { appId, language , vidhanSabhaNo, vidhanSabhaName , partyFullName} = useAppId();

    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            title: "Star Volunteers",
            subtitle: "Top performing karyakartas based on total points",
            loading: "Loading Leaderboard...",
            noVolunteers: "No volunteers found.",
            totalPoint: "Total Point:",
            volunteer: "Volunteer",
            call: "Call",
            starKaryakarta: "Star Karyakarta - ",
            phoneNA: "N/A",
            totalPointLabel: "Total Points",
            survey: "Survey",
            sleep: "Slip",
            message: "Message",
            photo: "Photo",
            contactUpdate: "Contact Update",
            audio: "Audio",
            video: "Video",
            selfie: "Selfie",
            callLabel: "Call",
            sms: "SMS",
            print: "Print",
            shareProfile: "Share",
            searchPlaceholder: "Search by name..."
        },
        hi: {
            title: "स्टार स्वयंसेवक",
            subtitle: "कुल अंकों के आधार पर शीर्ष प्रदर्शन करने वाले कार्यकर्ता",
            loading: "लीडरबोर्ड लोड हो रहा है...",
            noVolunteers: "कोई स्वयंसेवक नहीं मिला।",
            totalPoint: "कुल अंक:",
            volunteer: "स्वयंसेवक",
            call: "कॉल",
            starKaryakarta: "स्टार कार्यकर्ता - ",
            phoneNA: "N/A",
            totalPointLabel: "कुल अर्जित अंक",
            survey: "सर्वे",
            sleep: "स्लीप",
            message: "मैसेज",
            photo: "फ़ोटो",
            contactUpdate: "कॉन्टेक्ट अपडेट",
            audio: "ऑडियो",
            video: "वीडियो",
            selfie: "सेल्फी",
            callLabel: "कॉल",
            sms: "एस.एम.एस.",
            print: "प्रिन्ट",
            shareProfile: "शेयर करें",
            searchPlaceholder: "नाम से खोजें..."
        },
        gu: {
            title: "સ્ટાર સ્વયંસેવક",
            subtitle: "કુલ પોઈન્ટના આધારે ટોપ પરફોર્મિંગ કાર્યકર્તાઓ",
            loading: "લીડરબોર્ડ લોડ થઈ રહ્યું છે...",
            noVolunteers: "કોઈ સ્વયંસેવક મળ્યો નથી.",
            totalPoint: "કુલ પોઈન્ટ:",
            volunteer: "સ્વયંસેવક",
            call: "કૉલ",
            starKaryakarta: "સ્ટાર કાર્યકર્તા - ",
            phoneNA: "N/A",
            totalPointLabel: "કુલ મેળવેલા પોઈન્ટ",
            survey: "સર્વે",
            sleep: "સ્લીપ",
            message: "મેસેજ",
            photo: "ફોટો",
            contactUpdate: "કોન્ટેક્ટ અપડેટ",
            audio: "ઓડિયો",
            video: "વિડિયો",
            selfie: "સેલ્ફી",
            callLabel: "કૉલ",
            sms: "એસ.એમ.એસ.",
            print: "પ્રિંટ",
            shareProfile: "શેર કરો",
            searchPlaceholder: "નામ દ્વારા શોધો..."
        }
    };

    const txt = t[lang];

    // --- Modal State ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedVol, setSelectedVol] = useState(null);
    const [modalStats, setModalStats] = useState(null);
    const [modalUserDetails, setModalUserDetails] = useState(null);
    const [isSharing, setIsSharing] = useState(false);
    const modalRef = useRef(null);

    // --- 🔴 FIX: Prevent Background Scrolling when Modal is Open ---
    useEffect(() => {
        if (isModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isModalOpen]);

    // --- Local Storage State for Vidhan Sabha ---
    const [vsDetails, setVsDetails] = useState({ number: 'N/A', name: 'N/A' });

    // Load Vidhan Sabha from Local Storage on Mount
    useEffect(() => {
        const savedVS = localStorage.getItem("userVidhanSabha");
        if (savedVS) {
            try {
                const vsData = JSON.parse(savedVS);
                setVsDetails({
                    number: vsData.number || 'N/A',
                    name: vsData.name || 'N/A'
                });
            } catch (e) {
                console.error("Error parsing Vidhan Sabha from local storage");
            }
        }
    }, []);

    // Fetch initial list
    useEffect(() => {
        const fetchStarVolunteers = async () => {
            if (!appId) return;

            setLoading(true);
            try {
                const response = await axios.post(`${BASE_URL}/api/dis_star_karykarta`, {
                    app_id: appId
                });

                if (response.data.success && response.data.data && response.data.data.result) {
                    const sortedVolunteers = response.data.data.result.sort((a, b) => b.total - a.total);
                    setVolunteers(sortedVolunteers);
                } else {
                    setVolunteers([]);
                }
            } catch (error) {
                console.error("Error fetching star volunteers:", error);
                setVolunteers([]);
            } finally {
                setLoading(false);
            }
        };

        fetchStarVolunteers();
    }, [appId]);

    // Filter volunteers based on search term
    const filteredVolunteers = volunteers.filter(vol => {
        if (!searchTerm) return true;
        const lowerSearch = searchTerm.toLowerCase();
        return (
            (vol.name && vol.name.toLowerCase().includes(lowerSearch)) ||
            (vol.designation && vol.designation.toLowerCase().includes(lowerSearch)) ||
            (vol.mobile_no && vol.mobile_no.includes(lowerSearch))
        );
    });

    // Handle Card Click & Fetch Details
    const handleCardClick = async (vol, index) => {
        setSelectedVol({ ...vol, rank: index + 1 });
        setIsModalOpen(true);
        setModalLoading(true);
        setModalStats(null);
        setModalUserDetails(null); 

        try {
            const response = await axios.post(`${BASE_URL}/api/view_star_karykarta_detail`, {
                app_id: appId,
                user_id: vol.user_id
            });

            if (response.data.success && response.data.data) {
                if (response.data.data.result && response.data.data.result.length > 0) {
                    setModalStats(response.data.data.result[0]);
                }
                if (response.data.data.result2 && response.data.data.result2.length > 0) {
                    setModalUserDetails(response.data.data.result2[0]);
                }
            }
        } catch (error) {
            console.error("Error fetching volunteer details:", error);
        } finally {
            setModalLoading(false);
        }
    };

    // --- 🔴 FIX: Robust Image Sharing / Downloading ---
    const handleShareProfile = async () => {
        if (!modalRef.current) return;
        setIsSharing(true);
        
        try {
            await new Promise(resolve => setTimeout(resolve, 300));

            const canvas = await html2canvas(modalRef.current, {
                scale: 2,
                useCORS: true,
                allowTaint: false, // Ensures it doesn't break if cross-origin images fail
                backgroundColor: '#f8fafc' 
            });
            
            canvas.toBlob(async (blob) => {
                if (!blob) {
                    alert("Failed to generate image.");
                    setIsSharing(false);
                    return;
                }
                const file = new File([blob], `${selectedVol.name.replace(/\s+/g, '_')}_stats.png`, { type: 'image/png' });
                
                // Safely check if Web Share API is fully supported for files on this device
                let canShare = false;
                if (navigator.share && navigator.canShare && typeof navigator.canShare === 'function') {
                    canShare = navigator.canShare({ files: [file] });
                }
                
                if (canShare) {
                    try {
                        await navigator.share({
                            title: `${selectedVol.name} - Star Karyakarta`,
                            text: `Check out ${selectedVol.name}'s performance!`,
                            files: [file]
                        });
                    } catch (err) {
                        console.log('Share was canceled or failed:', err);
                    }
                } else {
                    // Fallback to Download if sharing is not supported
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${selectedVol.name.replace(/\s+/g, '_')}_stats.png`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setTimeout(() => URL.revokeObjectURL(url), 100);
                }
                setIsSharing(false);
            }, 'image/png');
        } catch (error) {
            console.error('Error creating image:', error);
            alert("Error generating image.");
            setIsSharing(false);
        }
    };

    const getAvatarSrc = (vol) => {
        let url = "";
        if (vol?.photo && vol.photo.startsWith('http')) {
            url = vol.photo;
        } else if (vol?.photo_path && vol.photo_path.trim() !== "") {
            url = vol.photo_path;
        } else {
            return "https://ui-avatars.com/api/?background=e2e8f0&color=475569&name=" + encodeURIComponent(vol?.name || "User");
        }
        return `${url}${url.includes('?') ? '&' : '?'}cb=${new Date().getTime()}`;
    };

    return (
        <div className='app-container p-4 custom-responsive-container'>
            
            <Header title={txt.title} />

            {/* Search Bar */}
            <div className="search-container mt-3 mb-4">
                <div className="search-wrapper">
                    <svg className="search-icon-main" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        className="search-input-modern"
                        type="text"
                        placeholder={txt.searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
                <div className="search-results-count" style={{
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    color: '#fbbf24',
                    padding: '8px 20px',
                    borderRadius: '30px',
                    border: '2px solid #f59e0b',
                    boxShadow: '0 0 20px rgba(245, 158, 11, 0.3)',
                    fontWeight: '700',
                    fontSize: '15px',
                    letterSpacing: '0.5px'
                }}>
                    {filteredVolunteers.length} {filteredVolunteers.length === 1 ? 'volunteer' : 'volunteers'}
                </div>
            </div>

            {loading && (
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loading}</p>
                </div>
            )}

            {!loading && filteredVolunteers.length === 0 && (
                <div className="text-center text-muted p-5">
                    {searchTerm ? `No volunteers found matching "${searchTerm}"` : txt.noVolunteers}
                </div>
            )}

            <div className="row g-4 pb-5 mt-1">
                {!loading && filteredVolunteers.map((vol, index) => {
                    const isFirst = index === 0;
                    let medal = " ";
                    if (index === 0) medal = "🥇";
                    else if (index === 1) medal = "🥈";
                    else if (index === 2) medal = "🥉";

                    return (
                        <div className="col-12 col-md-6 col-lg-4" key={vol.user_id || index}>
                            <div 
                                className="leaderboard-card clickable-card"
                                onClick={() => handleCardClick(vol, index)}
                            >
                                <div className="card-top">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="rank-text">
                                            {index + 1}
                                        </div>
                                        <div className={`points-badge ${isFirst ? 'points-gold' : 'points-silver'}`}>
                                            {txt.totalPoint} {vol.total || 0}
                                        </div>
                                    </div>
                                    <div className="medal-icon">
                                        {medal}
                                    </div>
                                </div>

                                <div className="card-bottom">
                                    <div className="d-flex align-items-center gap-3" style={{ flex: 1, minWidth: 0 }}>
                                        <img 
                                            src={getAvatarSrc(vol)} 
                                            alt={vol.name} 
                                            className="user-avatar"
                                            onError={(e) => {
                                                e.target.src = "https://ui-avatars.com/api/?background=e2e8f0&color=475569&name=" + encodeURIComponent(vol.name);
                                            }}
                                        />
                                        <div className="user-info">
                                            <h5 className="user-name text-truncate" title={vol.name}>{vol.name}</h5>
                                            <span className="user-designation text-truncate">{vol.designation || txt.volunteer}</span>
                                        </div>
                                    </div>

                                    {vol.mobile_no && (
                                        <a 
                                            href={`tel:${vol.mobile_no}`} 
                                            className="call-btn" 
                                            title={txt.call}
                                            onClick={(e) => e.stopPropagation()} 
                                        >
                                            <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
                                            </svg>
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Detail Pop Up Modal */}
            {isModalOpen && selectedVol && (
                <div className="custom-modal-overlay" onClick={() => !isSharing && setIsModalOpen(false)}>
                    <div className="custom-modal-content" onClick={(e) => e.stopPropagation()}>
                        
                        {/* 🔴 FIX: Sticky Button Container to Prevent Overlap */}
                        <div className="modal-button-section">
                            <button className="premium-share-btn" onClick={handleShareProfile} disabled={isSharing} title={txt.shareProfile}>
                                {isSharing ? (
                                    <div className="spinner-border spinner-border-sm" role="status"></div>
                                ) : (
                                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                        <circle cx="18" cy="5" r="3"></circle>
                                        <circle cx="6" cy="12" r="3"></circle>
                                        <circle cx="18" cy="19" r="3"></circle>
                                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                                    </svg>
                                )}
                            </button>
                            <button className="premium-close-btn" onClick={() => !isSharing && setIsModalOpen(false)}>
                                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                        
                        <div className="modal-split-layout" ref={modalRef}>
                            {/* --- LEFT SIDE: Profile --- */}
                            <div className="modal-left">
                                <div className={selectedVol.rank === 1 ? "modal-gold-header" : "modal-silver-header"}>
                                    {txt.starKaryakarta}{partyFullName}
                                </div>
                                <div className="modal-profile-body">
                                    <div className="avatar-wrapper">
                                        {selectedVol.rank === 1 && <span className="crown left-crown">👑</span>}
                                        <img 
                                            src={getAvatarSrc(selectedVol)} 
                                            alt={selectedVol.name} 
                                            className="modal-avatar"
                                            onError={(e) => { e.target.src = "https://ui-avatars.com/api/?background=e2e8f0&color=475569&name=" + encodeURIComponent(selectedVol.name); }}
                                        />
                                        {selectedVol.rank === 1 && <span className="crown right-crown">👑</span>}
                                    </div>
                                    <h3 className={`modal-name ${selectedVol.rank !== 1 ? 'silver-text' : ''}`}>{selectedVol.name}</h3>
                                    <p className="modal-designation">{selectedVol.designation || txt.volunteer}</p>
                                    <p className="modal-phone">{selectedVol.mobile_no || txt.phoneNA}</p>
                                </div>

                                <div className="vidhan-sabha-container mt-auto">
                                    <div className="d-flex align-items-center">
                                        <div className={`bjp-logo-wrapper ${selectedVol.rank !== 1 ? 'silver-border' : ''}`}>
                                            <img src={`https://maharashtra.bjpwings.com/img/admin/09022026022058_1770632573055.jpg?cb=${new Date().getTime()}`} alt="Party Logo" />
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '15px', overflow: 'hidden' }}>
                                            <div className="vidhan-sabha-text text-truncate" style={{ marginLeft: '0' }}>
                                                Ward No : {vidhanSabhaNo} 
                                            </div>
                                            <div style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '600' }} className="text-truncate">
                                                 {vidhanSabhaName}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={selectedVol.rank === 1 ? "gold-bottom-line" : "silver-bottom-line"}></div>
                                </div>
                            </div>

                            {/* --- RIGHT SIDE: Grid Stats matching the image --- */}
                            <div className="modal-right">
                                {modalLoading ? (
                                    <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                                        <div className="spinner-border text-primary" role="status"></div>
                                    </div>
                                ) : (
                                    <div className="stats-main-container w-100">
                                        <div className="stats-white-group">
                                            {/* Top Dark Header */}
                                            <div className="stats-total-header">
                                                <div className="total-label">{txt.totalPointLabel || "कुल अर्जित अंक"}</div>
                                                <div className="total-value">{selectedVol.total || 0}</div>
                                                <div className="total-divider"></div>
                                            </div>
                                            
                                            {/* Stats Grid */}
                                            <div className="stats-grid-container mt-3">
                                                {/* Row 1 - Two Columns */}
                                                <div className="stats-row-top mb-2">
                                                    <div className="stat-grid-card" style={{ flex: 1, backgroundColor: '#f0fdf4', borderColor: '#dcfce7' }}>
                                                        <div className="stat-grid-label" style={{ color: '#15803d' }}>{txt.survey}</div>
                                                        <div className="stat-grid-value" style={{ color: '#064e3b' }}>{modalStats?.SURVEY || 0}</div>
                                                    </div>
                                                    <div className="stat-grid-card" style={{ flex: 1, backgroundColor: '#eff6ff', borderColor: '#dbeafe' }}>
                                                        <div className="stat-grid-label" style={{ color: '#4338ca' }}>{txt.sleep}</div>
                                                        <div className="stat-grid-value" style={{ color: '#1e3a8a' }}>{modalStats?.SLEEP || 0}</div>
                                                    </div>
                                                </div>
                                                
                                                {/* Following Rows - 3 Columns */}
                                                <div className="stats-grid-3col">
                                                    <div className="stat-grid-card" style={{ backgroundColor: '#f0f9ff', borderColor: '#e0f2fe' }}>
                                                        <div className="stat-grid-label" style={{ color: '#2563eb' }}>{txt.message}</div>
                                                        <div className="stat-grid-value" style={{ color: '#1e3a8a' }}>{modalStats?.TEXT || 0}</div>
                                                    </div>
                                                    <div className="stat-grid-card" style={{ backgroundColor: '#faf5ff', borderColor: '#f3e8ff' }}>
                                                        <div className="stat-grid-label" style={{ color: '#9333ea' }}>{txt.photo}</div>
                                                        <div className="stat-grid-value" style={{ color: '#581c87' }}>{modalStats?.IMAGE || 0}</div>
                                                    </div>
                                                    <div className="stat-grid-card" style={{ backgroundColor: '#f0fdfa', borderColor: '#cffafe' }}>
                                                        <div className="stat-grid-label" style={{ color: '#0d9488' }}>{txt.contactUpdate}</div>
                                                        <div className="stat-grid-value" style={{ color: '#134e4a' }}>{modalStats?.["MOBILE UPDATE"] || 0}</div>
                                                    </div>
                                                    
                                                    <div className="stat-grid-card" style={{ backgroundColor: '#fdf2f8', borderColor: '#fce7f3' }}>
                                                        <div className="stat-grid-label" style={{ color: '#db2777' }}>{txt.audio}</div>
                                                        <div className="stat-grid-value" style={{ color: '#831843' }}>{modalStats?.AUDIO || 0}</div>
                                                    </div>
                                                    <div className="stat-grid-card" style={{ backgroundColor: '#fff1f2', borderColor: '#ffe4e6' }}>
                                                        <div className="stat-grid-label" style={{ color: '#e11d48' }}>{txt.video}</div>
                                                        <div className="stat-grid-value" style={{ color: '#881337' }}>{modalStats?.VIDEO || 0}</div>
                                                    </div>
                                                    <div className="stat-grid-card" style={{ backgroundColor: '#fefce8', borderColor: '#fef9c3' }}>
                                                        <div className="stat-grid-label" style={{ color: '#b45309' }}>{txt.selfie}</div>
                                                        <div className="stat-grid-value" style={{ color: '#78350f' }}>{modalStats?.SELFIE || 0}</div>
                                                    </div>
                                                    
                                                    <div className="stat-grid-card" style={{ backgroundColor: '#fff7ed', borderColor: '#ffedd5' }}>
                                                        <div className="stat-grid-label" style={{ color: '#ea580c' }}>{txt.callLabel}</div>
                                                        <div className="stat-grid-value" style={{ color: '#7c2d12' }}>{modalStats?.CALL || 0}</div>
                                                    </div>
                                                    <div className="stat-grid-card" style={{ backgroundColor: '#ecfeff', borderColor: '#cffafe' }}>
                                                        <div className="stat-grid-label" style={{ color: '#475569' }}>{txt.sms}</div>
                                                        <div className="stat-grid-value" style={{ color: '#0f172a' }}>{modalStats?.["SMS SLEEP"] || 0}</div>
                                                    </div>
                                                    <div className="stat-grid-card" style={{ backgroundColor: '#fff1f2', borderColor: '#ffe4e6' }}>
                                                        <div className="stat-grid-label" style={{ color: '#be123c' }}>{txt.print}</div>
                                                        <div className="stat-grid-value" style={{ color: '#881337' }}>{modalStats?.PRINT || 0}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                /* Search Bar Styles */
                .search-container {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 16px;
                    flex-wrap: wrap;
                }
                .search-wrapper {
                    position: relative;
                    flex: 1;
                    max-width: 450px;
                }
                .search-icon-main {
                    position: absolute;
                    left: 14px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    width: 20px;
                    height: 20px;
                    z-index: 1;
                }
                .search-input-modern {
                    width: 100%;
                    padding: 12px 42px 12px 42px;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    font-size: 14px;
                    color: #1e293b;
                    outline: none;
                    transition: all 0.2s ease;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                    background: white;
                }
                .search-input-modern:focus {
                    border-color: #2563eb;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
                }
                .clear-search-btn {
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: #f1f5f9;
                    border: none;
                    border-radius: 50%;
                    padding: 6px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #64748b;
                    transition: all 0.2s;
                }
                .clear-search-btn:hover {
                    background: #e2e8f0;
                }
                .search-results-count {
                    font-size: 14px;
                    font-weight: 600;
                    color: #475569;
                    white-space: nowrap;
                }

                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                .spinner { width: 50px; height: 50px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                .loader-text { color: #6B7280; font-weight: 500; font-size: 1.1rem; animation: pulse 1.5s ease-in-out infinite; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }

                .clickable-card { cursor: pointer; }
                .leaderboard-card {
                    background: #ffffff; border-radius: 20px; box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.05);
                    border: 1px solid #f1f5f9; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    display: flex; flex-direction: column; overflow: hidden; height: 100%;
                }
                .leaderboard-card:hover { transform: translateY(-6px); box-shadow: 0 20px 40px -5px rgba(0, 0, 0, 0.1); }

                .card-top { 
                    padding: 16px 24px; 
                    display: flex; 
                    justify-content: space-between; 
                    align-items: center; 
                    background: #ffffff;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .rank-text { 
                    font-size: 1.25rem; 
                    font-weight: 800; 
                    letter-spacing: 0.5px; 
                    color: #334155;
                }

                .points-badge { 
                    font-size: 1.05rem; 
                    font-weight: 700; 
                    padding: 6px 14px; 
                    border-radius: 50px; 
                }
                .points-gold {
                    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%); 
                    color: #ffffff;
                    box-shadow: 0 4px 10px rgba(245, 158, 11, 0.3);
                }
                .points-silver {
                    background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
                    color: #ffffff;
                    box-shadow: 0 4px 10px rgba(100, 116, 139, 0.3);
                }

                .medal-icon { font-size: 2.2rem; line-height: 1; filter: drop-shadow(0 4px 6px rgba(0,0,0,0.15)); }

                .card-bottom { display: flex; align-items: center; justify-content: space-between; padding: 24px; background: #ffffff; gap: 16px; }
                .user-avatar { width: 64px; height: 64px; border-radius: 50%; object-fit: cover; background-color: #f8fafc; border: 3px solid #ffffff; box-shadow: 0 4px 10px rgba(0,0,0,0.08); flex-shrink: 0; }
                .user-info { display: flex; flex-direction: column; overflow: hidden; }
                .user-name { margin: 0; font-size: 1.15rem; font-weight: 800; color: #0f172a; line-height: 1.2; }
                .user-designation { font-size: 0.8rem; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 6px; }

                .call-btn {
                    display: flex; align-items: center; justify-content: center; width: 48px; height: 48px;
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: #ffffff; border-radius: 50%;
                    text-decoration: none; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); flex-shrink: 0;
                    box-shadow: 0 6px 16px rgba(16, 185, 129, 0.3); 
                }
                .call-btn:hover { transform: scale(1.1) translateY(-2px); box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4); color: #ffffff; }

                /* =====================================
                   ENHANCED MODAL STYLES 
                   ===================================== */
                .custom-modal-overlay {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(15, 23, 42, 0.7); 
                    z-index: 9999; display: flex; justify-content: center; align-items: center;
                    backdrop-filter: blur(8px); 
                    padding: 20px;
                }
                .custom-modal-content {
                    background: #f8fafc; 
                    border-radius: 24px; 
                    width: 100%; max-width: 850px;
                    position: relative; overflow: hidden;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5); 
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    animation: modalFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                    max-height: 90vh;
                    overflow-y: auto;
                }
                @keyframes modalFadeIn {
                    from { opacity: 0; transform: scale(0.95) translateY(15px); }
                    to { opacity: 1; transform: scale(1) translateY(0); }
                }

                /* 🔴 FIX: MODAL BUTTON SECTION - NO OVERLAPPING */
                .modal-button-section {
                    position: sticky;
                    top: 0;
                    width: 100%;
                    display: flex;
                    justify-content: flex-end;
                    align-items: center;
                    gap: 12px;
                    z-index: 50;
                    background: #f8fafc;
                    padding: 12px 16px;
                    border-bottom: 1px solid #e2e8f0;
                }

                .premium-close-btn {
                    width: 36px;
                    height: 36px;
                    background: rgba(148, 163, 184, 0.15);
                    border: none;
                    border-radius: 50%;
                    color: #475569;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s;
                    flex-shrink: 0;
                }
                .premium-close-btn:hover {
                    background: #ef4444;
                    color: #ffffff;
                    transform: rotate(90deg);
                }

                .premium-share-btn {
                    width: 36px;
                    height: 36px;
                    background: #3b82f6;
                    color: #ffffff;
                    border: none;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s;
                    flex-shrink: 0;
                }
                .premium-share-btn:hover {
                    background: #2563eb;
                    color: #ffffff;
                    transform: scale(1.1);
                }
                .premium-share-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                    background: #94a3b8;
                }

                .modal-split-layout { 
                    display: flex; 
                    flex-direction: row; 
                    min-height: 420px; 
                    background: #f8fafc;
                }

                /* --- Modal Left Side (Profile) --- */
                .modal-left {
                    width: 42%; background: #ffffff; border-right: 1px solid #e2e8f0;
                    display: flex; flex-direction: column; position: relative;
                }
                .modal-gold-header {
                    background: linear-gradient(135deg, #b45309 0%, #d97706 50%, #f59e0b 100%); 
                    color: #ffffff; padding: 18px 16px; text-align: center;
                    font-weight: 800; font-size: 1.05rem; letter-spacing: 0.5px;
                }
                .modal-silver-header {
                    background: linear-gradient(135deg, #94a3b8 0%, #64748b 50%, #475569 100%);
                    color: #ffffff; padding: 18px 16px; text-align: center;
                    font-weight: 800; font-size: 1.05rem; letter-spacing: 0.5px;
                }

                .modal-profile-body {
                    padding: 40px 20px 20px 20px; display: flex; flex-direction: column;
                    align-items: center; text-align: center; flex-grow: 1;
                }
                .avatar-wrapper { position: relative; margin-bottom: 20px; }
                .modal-avatar {
                    width: 120px; height: 120px; border-radius: 50%; object-fit: cover;
                    border: 4px solid #ffffff; background: #f8fafc;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.1); 
                }
                .crown { position: absolute; font-size: 28px; top: -12px; filter: drop-shadow(0 4px 6px rgba(0,0,0,0.3)); }
                .left-crown { left: -18px; transform: rotate(-20deg); }
                .right-crown { right: -18px; transform: rotate(20deg); }

                .modal-name { color: #d97706; font-weight: 800; font-size: 1.5rem; margin: 0 0 6px 0; }
                .modal-name.silver-text { color: #475569; }
                .modal-designation { color: #334155; font-weight: 800; font-size: 1rem; margin: 0 0 8px 0; letter-spacing: 0.3px; }
                .modal-phone { color: #2563eb; font-weight: 700; margin: 0; font-size: 1.1rem; }

                /* Vidhan Sabha Footer Section */
                .vidhan-sabha-container {
                    padding: 20px 24px 28px 24px; 
                    background: #ffffff; position: relative; border-top: 1px solid #f1f5f9;
                    width: 100%;
                }
                .bjp-logo-wrapper {
                    width: 52px; height: 52px; border-radius: 50%;
                    background: radial-gradient(circle, #f7fee7 0%, #fef3c7 100%);
                    border: 3px solid #d97706; display: flex; align-items: center; justify-content: center;
                    box-shadow: 0 4px 10px rgba(217, 119, 6, 0.2); flex-shrink: 0;
                    overflow: hidden;
                    
                }
                .bjp-logo-wrapper.silver-border {
                    border-color: #94a3b8;
                    background: radial-gradient(circle, #f8fafc 0%, #e2e8f0 100%);
                    box-shadow: 0 4px 10px rgba(100, 116, 139, 0.2);
                }
                .bjp-logo-wrapper img { width: 55px; height: 55px; border-radius: 50%; object-fit: cover; background: #ffffff;}
                
                .vidhan-sabha-text { 
                    color: #ea580c; font-size: 1.2rem; font-weight: 900; 
                    margin-left: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
                }
                
                .gold-bottom-line {
                    position: absolute; bottom: 0; left: 0; width: 100%; height: 8px;
                    background: linear-gradient(90deg, #b45309 0%, #fcd34d 50%, #b45309 100%);
                }
                .silver-bottom-line {
                    position: absolute; bottom: 0; left: 0; width: 100%; height: 8px;
                    background: linear-gradient(90deg, #64748b 0%, #cbd5e1 50%, #64748b 100%);
                }

                /* --- Modal Right Side (Image Grid Stats Layout) --- */
                .modal-right {
                    width: 58%; padding: 24px; display: flex; align-items: flex-start;
                    background-color: #f8fafc; overflow-y: auto;
                }
                
                .stats-main-container {
                    width: 100%;
                }

                .stats-white-group {
                    background-color: #ffffff;
                    padding: 20px;
                    border-radius: 20px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
                }

                .stats-total-header {
                    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
                    border-radius: 16px;
                    padding: 24px 20px;
                    text-align: center;
                    position: relative;
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
                    margin-bottom: 20px;
                    border: 1px solid rgba(255, 255, 255, 0.05);
                }
                
                .total-label {
                    color: #94a3b8;
                    font-size: 13px;
                    font-weight: 600;
                    margin-bottom: 6px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                
                .total-value {
                    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    font-size: 56px;
                    font-weight: 900;
                    line-height: 1.1;
                    letter-spacing: -1px;
                    text-shadow: 0 0 30px rgba(245, 158, 11, 0.3);
                }
                
                .total-divider {
                    width: 60px;
                    height: 4px;
                    background: linear-gradient(90deg, #fbbf24, #f59e0b);
                    border-radius: 4px;
                    margin: 16px auto 0;
                    box-shadow: 0 0 15px rgba(245, 158, 11, 0.3);
                }

                /* Grid Layout for Cards */
                .stats-row-top {
                    display: flex;
                    gap: 12px;
                }

                .stats-grid-3col {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 12px;
                }

                .stat-grid-card {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 16px 10px;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                    transition: all 0.2s ease;
                }

                .stat-grid-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
                }

                .stat-grid-label {
                    font-size: 11px;
                    font-weight: 700;
                    margin-bottom: 8px;
                    letter-spacing: 0.5px;
                }

                .stat-grid-value {
                    font-size: 24px;
                    font-weight: 900;
                    line-height: 1;
                }

                /* =====================================
                   RESPONSIVE MEDIA QUERIES
                   ===================================== */
                @media (max-width: 992px) {
                    .modal-left { width: 45%; }
                    .modal-right { width: 55%; padding: 20px; }
                }

                @media (max-width: 768px) {
                    .custom-responsive-container { padding: 10px !important; }
                    .card-bottom { padding: 16px; }
                    .user-avatar { width: 50px; height: 50px; }
                    .call-btn { width: 40px; height: 40px; }
                    .medal-icon { font-size: 1.8rem; }
                    
                    /* Modal Stacking for Mobile */
                    .modal-split-layout { 
                        flex-direction: column; 
                        min-height: auto; 
                        border-radius: 24px;
                    }
                    .modal-left { width: 100%; border-right: none; border-bottom: 1px solid #e2e8f0; }
                    .modal-right { width: 100%; padding: 20px 15px; }
                    
                    .modal-avatar { width: 100px; height: 100px; }
                    .modal-name { font-size: 1.3rem; }
                    .vidhan-sabha-container { padding: 15px 20px 20px 20px; }
                    .vidhan-sabha-text { font-size: 1.1rem; }
                    
                    /* FIX FOR OVERLAPPING BUTTONS ON MOBILE */
                    .modal-button-section {
                        padding: 10px 12px;
                        gap: 10px;
                    }

                    .premium-close-btn, .premium-share-btn { 
                        width: 32px !important;
                        height: 32px !important;
                    }
                    
                    /* Mobile Grid Adjustments */
                    .stats-grid-3col {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    /* Search Bar Mobile */
                    .search-container {
                        flex-direction: column;
                        align-items: stretch;
                    }
                    .search-wrapper {
                        max-width: 100%;
                    }
                    .search-results-count {
                        text-align: right;
                        font-size: 13px;
                    }
                }

                @media (max-width: 480px) {
                    .rank-text { font-size: 1.1rem; }
                    .points-badge { font-size: 0.95rem; }
                    .total-value { font-size: 42px; }
                    .stat-grid-value { font-size: 20px; }
                    .search-input-modern {
                        padding: 10px 36px 10px 36px;
                        font-size: 13px;
                    }
                    .search-icon-main {
                        width: 16px;
                        height: 16px;
                        left: 10px;
                    }
                }
            `}</style>
        </div>
    );
};

export default StarVolunteer;